"use client";

import type {ApprovalDecisionOutcome} from "@/types/chatMessages";
import type {MutationProposal} from "@/services/wingman/mutation-proposal";

import clsx from "clsx";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import Icon from "@/components/library/Icon";
import type {MessageContentFunctionCall, ToolApprovalResponse} from "@/services/wingman";
import MutationProposalReviewBar from "./MutationProposalReviewBar";
import UseCaseSnapshotDiff from "./UseCaseSnapshotDiff";
import ScriptSnapshotDiff, {changedFields} from "./ScriptSnapshotDiff";
import ToolApprovalNotices from "./ToolApprovalNotices";
import {useToolApprovalDecision} from "./useToolApprovalDecision";

const EXPIRY_WARNING_MS = 2 * 60 * 1000;

const formatRemaining = (ms: number) => {
   const seconds = Math.ceil(ms / 1000);
   return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

function expiryClass(expired: boolean, remainingMs: number): string {
   if (expired) return "bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300";
   if (remainingMs <= EXPIRY_WARNING_MS) return "bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-200";
   return "text-500";
}

function ProposalExpiry({expired, remainingMs}: {expired: boolean; remainingMs?: number}) {
   const {t} = useTranslation("ask");
   if (remainingMs === undefined) return null;
   const time = formatRemaining(remainingMs);
   const expiresIn = t("toolApproval.expiresIn", {time});
   return <span
      aria-label={expired ? t("toolApproval.expiredShort") : expiresIn}
      title={expired ? t("toolApproval.expiredDetail") : expiresIn}
      className={clsx("mr-1 inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs tabular-nums", expiryClass(expired, remainingMs))}>
      <Icon icon="clock" className="h-3 w-3" />
      {expired ? t("toolApproval.expiredShort") : time}
   </span>;
}

/**
 * The review surface on the edited target's page: a floating decision bar, then exactly what
 * would change. A script editor below keeps showing the script as it is today; an Action
 * review replaces its editor until closed, so it opens what was saved once execution lands.
 */
export default function MutationProposalReviewPanel({call, completed, result, active, disabled, onRespond, onRequestFresh, onClose}: {
   call: MessageContentFunctionCall;
   completed: boolean;
   result?: unknown;
   active: boolean;
   disabled: boolean;
   onRespond?: (decision: ToolApprovalResponse) => Promise<ApprovalDecisionOutcome>;
   onRequestFresh?: (proposal: MutationProposal) => Promise<void>;
   onClose: () => void;
}) {
   const {t} = useTranslation("ask");
   const changesRef = useRef<HTMLElement>(null);
   const {status, settled, expired, error, ownerOnly, queued, canRequestFresh, requestFresh, requestedFresh, pending, canRespond, canApply, respond, remainingMs, saved} =
      useToolApprovalDecision({call, completed, result, active, disabled, onRespond, onRequestFresh});
   const proposal = call.approval?.proposal;
   if (!proposal) return null;

   const changeCount = changedFields(proposal).length;
   const actionable = active && !settled;
   let applyIcon;
   if (pending === "apply") applyIcon = <Icon icon="spinner" className="h-3 w-3 animate-spin" />;
   else if (queued === true) applyIcon = <Icon icon="check" className="h-3 w-3" />;

   return <>
      <MutationProposalReviewBar onClose={onClose}
         title={<h2 className="text-sm font-semibold text-900">{t("toolApproval.suggestedChanges")}</h2>}
         subtitle={<p className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-500">
            <span className="truncate">{proposal.summary}</span>
            {changeCount > 0 && <>
               <span aria-hidden>·</span>
               <button type="button" onClick={() => changesRef.current?.scrollIntoView({behavior: "smooth", block: "start"})}
                  className="shrink-0 font-medium text-700 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary">
                  {t("toolApproval.changeCount", {count: changeCount})}
               </button>
            </>}
         </p>}
         actions={<>
            {actionable && <ProposalExpiry expired={expired} remainingMs={remainingMs} />}
            {actionable ? <>
               <Button type="tertiary" compact disabled={!canRespond} aria-pressed={queued === false} onClick={() => void respond(false)}
                  iconBefore={queued === false ? <Icon icon="check" className="h-3 w-3" /> : undefined}>
                  {pending === "discard" ? t("toolApproval.discarding") : t("toolApproval.discard")}
               </Button>
               <Button type="primary" compact disabled={!canApply} aria-pressed={queued === true} onClick={() => void respond(true)}
                  iconBefore={applyIcon}>
                  {pending === "apply" ? t("toolApproval.applying") : t("toolApproval.applyChanges")}
               </Button>
            </> : <output className="text-xs font-medium text-600">{t(`toolApproval.${status}`)}</output>}
         </>}>
         <ToolApprovalNotices status={status} settled={settled} expired={expired} error={error} ownerOnly={ownerOnly} canRequestFresh={canRequestFresh} onRequestFresh={requestFresh} requestedFresh={requestedFresh} queued={queued} className="mt-2 text-xs" />
      </MutationProposalReviewBar>

      <section ref={changesRef} aria-label={t("toolApproval.title")} className="@container mb-3 scroll-mt-28 overflow-hidden rounded-2xl border border-neutral bg-surface">
         {proposal.capability === "use_cases_update" ? <UseCaseSnapshotDiff preview={proposal} saved={saved} savedOpen /> : <ScriptSnapshotDiff preview={proposal} />}
      </section>

      {proposal.capability !== "use_cases_update" && <div className="mb-3 flex items-center gap-3 pt-1 text-xs text-500" role="separator">
         <span className="h-px flex-1 bg-neutral" />
         <span className="shrink-0">{t(actionable ? "toolApproval.currentScript" : "toolApproval.currentScriptSettled")}</span>
         <span className="h-px flex-1 bg-neutral" />
      </div>}
   </>;
}
